import { Component, OnInit } from '@angular/core';
import { UnitOfWorkService } from '../../../services/unit-of-work/unit-of-work.service';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { ApplicationInsightsService } from '../../../services/application-insights/application-insights.service';
import { DialogHelper } from '../../../services/dialog-helper';
import { IsBusyService } from '../../../services/is-busy/is-busy.service';
import { GlobalErrorHandler } from '../../../services/global-error-handler';
import * as model from '../../../../model/model';

const loadingKey: string = 'inspection-engagements:loadData';
const saveKey: string = 'inspection-engagements:save';
const defaultLimit: number = 15;

@Component({
    selector: 'app-inspection-engagements',
    templateUrl: './inspection-engagements.component.html',
    standalone: false
})
export class InspectionEngagementsComponent implements OnInit {
    isLoading: boolean = false;

    engagements: model.InspectionEngagement[] = [];

    page: number = 1;
    limit: number = defaultLimit;
    pageLimitOptions = [15, 25, 50, 100];

    codeFilter: string = null;
    clientFilter: string = null;
    officeFilter: string = null;
    regionFilter: string = null;
    yearFilter: string = null;

    constructor(
        private uow: UnitOfWorkService,
        private appInsights: ApplicationInsightsService,
        private errorHandler: GlobalErrorHandler,
        private activeRoute: ActivatedRoute,
        public isBusy: IsBusyService,
        public dialogHelper: DialogHelper
    ) {
        appInsights.logPageView();
    }

    ngOnInit() {
        this.activeRoute.queryParams.subscribe(routeParams => {
            this.page = routeParams.page && !Number.isNaN(routeParams.page) ? parseInt(routeParams.page) : 1;
            this.limit = routeParams.limit && !Number.isNaN(routeParams.limit) ? parseInt(routeParams.limit) : defaultLimit;
        });

        this.loadData();
    }

    canDeactivate(): Observable<boolean> | boolean {
        if (this.uow.hasChanges.value) {
            const proceed = this.dialogHelper.confirm();

            proceed.subscribe(result => {
                if (result) {
                    this.uow.rollback();
                }
            });

            return proceed;
        }

        return true;
    }

    loadData() {
        this.isLoading = true;
        this.isBusy.set(loadingKey, true, 'Loading data');

        return this.uow.getInspectionEngagements()
            .then(result => {
                this.engagements = result;
            })
            .catch(reason => {
                this.errorHandler.handleError(reason);
            })
            .finally(() => {
                this.isLoading = false;
                this.isBusy.set(loadingKey, false);
            });
    }

    private matchesFilter(value: string, filter: string): boolean {
        if (!filter) {
            return true;
        }
        return (value || '').toLowerCase().includes(filter.toLowerCase());
    }

    get filteredEngagements(): model.InspectionEngagement[] {
        return this.engagements
            .filter(m => !m.entityAspect.entityState.isDeleted() && !m.entityAspect.entityState.isDetached())
            .filter(m => this.matchesFilter(m.code, this.codeFilter))
            .filter(m => this.matchesFilter(m.client ? m.client.name : null, this.clientFilter))
            .filter(m => this.matchesFilter(m.office ? m.office.name : null, this.officeFilter))
            .filter(m => this.matchesFilter(m.regionCode, this.regionFilter))
            .filter(m => this.matchesFilter(m.inspectionYear != null ? m.inspectionYear.toString() : null, this.yearFilter))
            .sort((a, b) => {
                if (b.inspectionYear !== a.inspectionYear) {
                    return (b.inspectionYear || 0) - (a.inspectionYear || 0);
                }
                return (a.code || '').localeCompare(b.code || '');
            });
    }

    get pageCount(): number {
        return Math.max(1, Math.ceil(this.filteredEngagements.length / this.limit));
    }

    get pagedEngagements(): model.InspectionEngagement[] {
        const start = (this.page - 1) * this.limit;
        return this.filteredEngagements.slice(start, start + this.limit);
    }

    clearFilters() {
        this.codeFilter = null;
        this.clientFilter = null;
        this.officeFilter = null;
        this.regionFilter = null;
        this.yearFilter = null;
    }

    destroyEngagement(engagement: model.InspectionEngagement) {
        if (!engagement) {
            return;
        }

        if (confirm('Are you sure you want to remove "' + engagement.code + '"?')) {
            this.uow.inspectionEngagements.cancelChanges(engagement);
            engagement.entityAspect.setDeleted();

            this.isBusy.set(saveKey, true, 'Removing inspection engagement "' + engagement.code + '"');
            this.uow.inspectionEngagements.saveChanges([engagement])
                .catch(reason => {
                    this.errorHandler.handleError(reason);
                })
                .finally(() => {
                    this.isBusy.set(saveKey, false);
                });
        }
    }
}
